/// <reference types="vite/client" />

/**
 * HTTP client for API communication
 * Type-safe, with proper error handling and no sensitive data logging
 */

import { ApiError } from './types';
import type { EncryptedData } from '../crypto';

const API_BASE_URL = (import.meta.env['VITE_API_URL'] as string | undefined) || '';

/**
 * Make a JSON API request
 * @param method - HTTP method
 * @param path - API path (relative to base URL)
 * @param body - Request body (optional)
 * @returns Parsed JSON response
 * @throws ApiError for non-2xx responses
 */
export async function apiRequest<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown
): Promise<T> {
  const url = `${API_BASE_URL}${path}`;

  const options: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for authentication
  };

  if (body !== undefined) {
    options.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, options);

    // Try to parse response as JSON
    let responseBody: unknown;
    try {
      responseBody = await response.json();
    } catch {
      responseBody = null;
    }

    // Non-2xx response
    if (!response.ok) {
      const error = new Error(
        `API request failed: ${response.status} ${response.statusText}`
      ) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      error.body = responseBody;
      throw error;
    }

    // 2xx response
    return responseBody as T;
  } catch (error) {
    // Re-throw ApiError as-is
    if (error instanceof Error && 'status' in error) {
      throw error;
    }

    // Network error or other fetch failure
    const err = new Error(`API request failed: ${String(error)}`) as ApiError;
    err.status = 0;
    err.statusText = 'Network Error';
    err.body = null;
    throw err;
  }
}

/**
 * Create a secret on the server
 * @param secret - Encrypted secret data
 * @param hint - Optional encrypted hint
 * @param ttl - Optional TTL in seconds (default: 1 hour)
 * @param sendNotification - Whether to send burn notification to authenticated user's email
 * @returns Secret ID and expiration time
 */
export async function createSecret(
  secret: EncryptedData,
  hint?: EncryptedData | null,
  ttl?: number,
  sendNotification?: boolean
): Promise<{ secretId: string; expiresAt: string }> {
  const body: {
    secret: EncryptedData;
    hint?: EncryptedData;
    ttl: number;
    send_burn_notification?: boolean;
  } = {
    secret,
    ttl: ttl || 3600, // Default 1 hour
  };

  if (hint) {
    body.hint = hint;
  }

  if (sendNotification) {
    body.send_burn_notification = true;
  }

  const response = await apiRequest<{
    secret_id: string;
    expires_at: string;
  }>('POST', '/api/secrets', body);

  return {
    secretId: response.secret_id,
    expiresAt: response.expires_at,
  };
}

/**
 * Get secret hint and metadata (without retrieving encrypted content)
 * @param secretId - Secret ID
 * @returns Secret hint (encrypted)
 */
export async function getSecretHint(
  secretId: string
): Promise<{ secretId: string; hint: EncryptedData | null }> {
  const response = await apiRequest<{
    secret_id: string;
    hint: EncryptedData | null;
  }>('GET', `/api/secrets/${secretId}`);

  return {
    secretId: response.secret_id,
    hint: response.hint,
  };
}

/**
 * Get secret metadata (without retrieving encrypted content)
 * @param secretId - Secret ID
 * @returns HTML page with metadata (used by browser, not typically called from JS)
 */
export async function getSecretMetadata(secretId: string): Promise<Response> {
  const url = `${API_BASE_URL}/api/secrets/${secretId}`;
  const response = await fetch(url);

  if (!response.ok) {
    const error = new Error(`Failed to get secret: ${response.status}`) as ApiError;
    error.status = response.status;
    error.statusText = response.statusText;
    error.body = null;
    throw error;
  }

  return response;
}

/**
 * Retrieve encrypted secret from server (atomic read-delete)
 * @param secretId - Secret ID
 * @returns Encrypted secret and hint
 */
export async function retrieveSecret(
  secretId: string
): Promise<{ secret: EncryptedData; hint: EncryptedData | null }> {
  const response = await apiRequest<{
    secret: EncryptedData;
    hint: EncryptedData | null;
  }>('POST', `/api/secrets/${secretId}/retrieve`, {});

  return {
    secret: response.secret,
    hint: response.hint,
  };
}

/**
 * Get version information from the server
 * @returns Version, commit SHA, and build date
 */
export async function getVersion(): Promise<{
  version: string;
  commit: string;
  buildDate: string;
}> {
  const response = await apiRequest<{
    version: string;
    commit: string;
    build_date: string;
  }>('GET', '/api/version');

  return {
    version: response.version,
    commit: response.commit,
    buildDate: response.build_date,
  };
}

/**
 * Report abuse for a secret
 * @param secretId - Secret ID to report
 * @param reason - Reason for report
 * @param details - Optional additional details
 * @returns Report ID and creation timestamp
 */
export async function reportAbuse(
  secretId: string,
  reason: string,
  details?: string
): Promise<{ reportId: string; message: string; createdAt: string }> {
  const body: {
    secret_id: string;
    reason: string;
    details?: string;
  } = {
    secret_id: secretId,
    reason,
  };

  if (details && details.trim() !== '') {
    body.details = details;
  }

  const response = await apiRequest<{
    report_id: string;
    message: string;
    created_at: string;
  }>('POST', '/api/abuse/report', body);

  return {
    reportId: response.report_id,
    message: response.message,
    createdAt: response.created_at,
  };
}
